import React, {
  useContext,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import classnames from 'clsx';
import type { ToolbarChildrenProps } from 'draft-js-inline-toolbar-plugin';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import LinkModal from '../../../../../../RichTextBoxLinkModal/viewer/RichTextBoxLinkModal';
import styles from '../../button.scss';
import LinkIcon from '../LinkIcon';
import { hasEntity } from '../../../../utils/draftUtils';
import StylesContext from '../../../../StylesContext';
import semanticClassNames from '../../../../../RichTextBox.semanticClassNames';
import type {
  IRichTextBoxMapperProps,
  OpenModalRef,
} from '../../../../../RichTextBox.types';
import Tooltip from '../../Tooltip';
import { useActionTranslations } from '../../../../useActionTranslations';

const InlineLinkButton = forwardRef<
  HTMLButtonElement,
  ToolbarChildrenProps & {
    linkModalButtonRef: React.RefObject<OpenModalRef>;
    translations: IRichTextBoxMapperProps['translations'];
    linkModalId: IRichTextBoxMapperProps['linkModalId'];
    shouldShowTooltips: boolean;
  }
>((props, ref) => {
  const {
    setEditorState,
    getEditorState,
    onOverrideContent,
    translations,
    shouldShowTooltips,
    linkModalId,
  } = props;
  const stylesContext = useContext(StylesContext);
  const linkButtonRef = useRef<HTMLDivElement>(null);
  const { toolbarLinkAction } = useActionTranslations(translations);

  const onClose = useCallback(() => {
    // @ts-expect-error
    onOverrideContent(undefined);
  }, [onOverrideContent]);

  const openLinkModal = useCallback(() => {
    setTimeout(() => {
      const content = () => (
        <LinkModal
          getEditorState={getEditorState}
          setEditorState={setEditorState}
          onClose={onClose}
          linkButtonRef={linkButtonRef}
          id={linkModalId}
          translations={translations}
        />
      );
      onOverrideContent(content);
    }, 0);
  }, [
    getEditorState,
    onOverrideContent,
    setEditorState,
    onClose,
    linkModalId,
    translations,
  ]);

  useImperativeHandle(props.linkModalButtonRef, () => ({
    openModal: openLinkModal,
  }));

  const preventBubblingUp = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const selected = hasEntity(getEditorState(), 'LINK');
  return (
    <div className={styles.buttonContainer} ref={linkButtonRef}>
      <Tooltip shouldShow={shouldShowTooltips} content={toolbarLinkAction}>
        <button
          className={classnames(styles.button, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onMouseDown={preventBubblingUp}
          onClick={openLinkModal}
          type="button"
          ref={ref}
        >
          <div
            className={classnames(
              styles.buttonContent,
              formatClassNames(semanticClassNames.icons),
              selected && styles.buttonContentSelected,
            )}
          >
            <LinkIcon />
          </div>
        </button>
      </Tooltip>
    </div>
  );
});

export default InlineLinkButton;
